import React from 'react';
import { Clock, ShieldCheck, Users, RefreshCw, Plug } from 'lucide-react';
import Reveal from './Reveal';

// Business-language summary for the people who sign off (plant managers, ops,
// finance). Every claim here mirrors what the technical sections below prove.
const OUTCOMES = [
  {
    icon: Clock,
    title: 'Plans in hours instead of weeks',
    body: "A layout study usually takes weeks of specialist time. The Factory Agent produces a first draft of the whole line in about 30 minutes: layout, line balance, shift schedule and robot programs. Your team spends its time deciding instead of drawing.",
  },
  {
    icon: ShieldCheck,
    title: 'Mistakes found before you spend',
    body: 'Every layout is checked for clearances and safety zones, every schedule is simulated, and every robot program is verified before anything is ordered or installed. Problems show up on screen, where fixing them is cheap.',
  },
  {
    icon: Users,
    title: 'Less dependence on scarce specialists',
    body: "Your engineers describe what they need in plain language and review the result. The agent does the layout, balancing, scheduling and robot programming that would otherwise wait for an expert's calendar.",
  },
  {
    icon: RefreshCw,
    title: 'Ready when demand changes',
    body: 'A new order, a lost shift, a new product variant: re-plan by asking, and get an updated balance and schedule the same day instead of starting a new study.',
  },
  {
    icon: Plug,
    title: 'Works with what you run today',
    body: 'It sits alongside your ERP and MES and hands back standard files: STEP, DXF, Excel and vendor-native robot code. You can start with one line and the data you already have.',
  },
];

const OutcomesSection = () => (
  <section id="outcomes" className="py-28 bg-white dark:bg-black">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <Reveal>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <div className="sm:col-span-2 lg:col-span-1 lg:pr-6 mb-4 lg:mb-0">
            <div className="font-mono text-xs tracking-[0.25em] uppercase text-emerald-600 dark:text-emerald-400 mb-4">
              For the people who sign off
            </div>
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight text-gray-900 dark:text-white mb-5">
              What changes for your operation
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Faster plans, fewer expensive surprises, and less waiting on specialists.
              The sections below show how each one is proven.
            </p>
          </div>
          {OUTCOMES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              className="rounded-2xl border border-black/10 dark:border-white/10 bg-black/[0.02] dark:bg-white/[0.03] p-7"
            >
              <Icon className="text-emerald-600 dark:text-emerald-400 mb-5" size={26} strokeWidth={1.75} aria-hidden="true" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default OutcomesSection;
